/**
 * Execution detail slice — data for the /execution page.
 *
 * - fetchExecutionDetail: POST /executions/detail via beaconApi, mapped to the
 *   ExecutionDetailView view model. When several executions match, the one whose
 *   id equals the searched id is shown, else the latest (last by createdAt).
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ExecutionDetailView } from './executionDetailTypes';
import { mapExecutionDetail } from './executionDetailMapper';
import { beaconApi } from '../../../services/beaconApi';
import { RootState } from '../../store';

export interface ExecutionDetailState {
  data: ExecutionDetailView | null;
  /** Other executions returned for the same identifier. */
  otherExecutionIds: string[];
  loading: boolean;
  error: string | null;
  notFound: boolean;
  currentRequestId: string | null;
  /** Entity id of the last fetch; re-dispatched on region change. */
  currentId: string | null;
}

export const executionDetailInitialState: ExecutionDetailState = {
  data: null,
  otherExecutionIds: [],
  loading: false,
  error: null,
  notFound: false,
  currentRequestId: null,
  currentId: null,
};

interface FetchResult {
  detail: ExecutionDetailView | null;
  otherExecutionIds: string[];
}

export const fetchExecutionDetail = createAsyncThunk<
  FetchResult, string, { state: RootState; rejectValue: string }
>('executionDetail/fetch', async (executionId, { getState, rejectWithValue }) => {
  const { apiBase } = getState().env;
  try {
    const response = await beaconApi.getExecutionDetail([executionId], apiBase);
    const found = response.executions ?? [];
    if (found.length === 0) return { detail: null, otherExecutionIds: [] };
    const primary = found.find(execution => execution.id === executionId) ?? found[found.length - 1];
    return {
      detail: mapExecutionDetail(primary),
      otherExecutionIds: found.filter(execution => execution !== primary).map(execution => execution.id),
    };
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch execution detail');
  }
});

const executionDetailSlice = createSlice({
  name: 'executionDetail',
  initialState: executionDetailInitialState,
  reducers: {
    clearExecutionDetail: () => executionDetailInitialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExecutionDetail.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.notFound = false;
        state.currentRequestId = action.meta.requestId;
        state.currentId = action.meta.arg;
      })
      .addCase(fetchExecutionDetail.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.data = action.payload.detail;
        state.otherExecutionIds = action.payload.otherExecutionIds;
        state.notFound = action.payload.detail === null;
      })
      .addCase(fetchExecutionDetail.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to fetch execution detail';
      });
  },
});

export const { clearExecutionDetail } = executionDetailSlice.actions;
export default executionDetailSlice.reducer;
